import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AdminIcon, FilterButton } from '../admin-kit';
import './huisstijl.css';

export interface FilterOption<V extends string> {
  value: V;
  label: ReactNode;
}

/**
 * Een filterknop in de werkbalk met een keuzelijst eronder ("Status", "Soort", "Toon: Alle").
 *
 * De kit heeft de knop (`FilterButton`) en laat het scherm kiezen wat er bij een klik opent; dit is
 * het kleinste dat werkt: een menu met één keuze, Escape en klikken ernaast sluiten het. De eerste
 * optie is "alles" en zet de knop terug in zijn rusttoestand.
 */
export function FilterMenu<V extends string>({
  label,
  options,
  value,
  onChange,
  showValue,
  testId,
}: {
  label: ReactNode;
  options: FilterOption<V>[];
  value: V;
  onChange: (value: V) => void;
  /** De gekozen waarde ook in rust tonen ("Toon: **Alle**"), zoals de mockup bij Toon doet. */
  showValue?: boolean;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const first = options[0]?.value;
  const chosen = options.find((option) => option.value === value);
  const active = value !== first;

  // `FilterButton` geeft geen ref door; de knop is het enige `.a-filter` in deze wrapper.
  const focusButton = () => wrap.current?.querySelector<HTMLButtonElement>('.a-filter')?.focus();

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      focusButton();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // De focus naar de gekozen optie, zodat pijltjes en Enter meteen werken.
    wrap.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const move = (delta: number) => {
    const items = Array.from(
      wrap.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? [],
    );
    const index = items.indexOf(document.activeElement as HTMLElement);
    items[(index + delta + items.length) % items.length]?.focus();
  };

  return (
    <div className="a-filter-wrap" ref={wrap}>
      <FilterButton
        label={label}
        value={showValue || active ? chosen?.label : undefined}
        active={active}
        expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        testId={testId}
        onClick={() => setOpen((current) => !current)}
      />
      {open ? (
        <div
          className="a-menu"
          role="menu"
          id={menuId}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(1);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(-1);
            }
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={option.value === value}
              className="a-menu-item"
              onClick={() => {
                onChange(option.value);
                setOpen(false);
                focusButton();
              }}
            >
              {option.label}
              {option.value === value ? (
                <span className="a-menu-right">
                  <AdminIcon name="check" size={16} />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
